import {
    createContext,
    useContext,
    useState,
    useCallback,
    type ReactNode,
} from 'react';

/**
 * Map of PHP API paths to their Go equivalents.
 * Add new entries here as more endpoints are implemented in Go.
 */
export const GO_API_PATHS: Record<string, string> = {
    '/api/profile/me': '/api/go/profile/me',
    '/api/auth/status': '/api/go/auth/status',
    '/api/collections': '/api/go/collections',
    '/api/tags': '/api/go/tags',
};

/**
 * Standalone path resolver — reads directly from localStorage.
 * Use this in non-component code (e.g. queryFn callbacks).
 * Go routing is the default; storing 'false' (admin bolt toggle) opts back into PHP.
 */
export function resolveApiPath(path: string): string {
    if (typeof window === 'undefined') return path;
    if (localStorage.getItem('goApiEnabled') === 'false') return path;
    return GO_API_PATHS[path] ?? path;
}

interface GoApiContextType {
    goApiEnabled: boolean;
    toggleGoApi: () => void;
    resolveApiPath: (path: string) => string;
}

const GoApiContext = createContext<GoApiContextType | null>(null);

function getStoredGoApiPreference(): boolean {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('goApiEnabled') !== 'false';
}

export function GoApiProvider({ children }: { children: ReactNode }) {
    const [goApiEnabled, setGoApiEnabled] = useState<boolean>(
        getStoredGoApiPreference,
    );

    const toggleGoApi = () => {
        setGoApiEnabled((prev) => {
            const newValue = !prev;
            localStorage.setItem('goApiEnabled', String(newValue));
            return newValue;
        });
    };

    const resolveApiPath = useCallback(
        (path: string): string => {
            if (!goApiEnabled) return path;
            return GO_API_PATHS[path] ?? path;
        },
        [goApiEnabled],
    );

    return (
        <GoApiContext.Provider value={{ goApiEnabled, toggleGoApi, resolveApiPath }}>
            {children}
        </GoApiContext.Provider>
    );
}

export function useGoApi(): GoApiContextType {
    const context = useContext(GoApiContext);
    if (!context) {
        throw new Error('useGoApi must be used within a GoApiProvider');
    }
    return context;
}
